import React from 'react';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import { useTeamTab } from '../../TeamTabContext';

export const RolesAndCategoriesPanel: React.FC = () => {
  const {
    showManageRoles,
    canManageOrgTeam,
    rolesList,
    availableRoles,
    loadRoles,
    handleToggleRoleInMapping,
    handleChangeCapacityCategory,
    handleRenameRole,
    handleDeleteRole,
    editingRoleId,
    setEditingRoleId,
    editingRoleName,
    setEditingRoleName,
    renameInputRef,
  } = useTeamTab();

  if (!showManageRoles && canManageOrgTeam) return null;

  return (
    <div
      style={{
        marginBottom: '16px',
        padding: '12px 16px',
        backgroundColor: 'var(--profile-nested-elevated-bg)',
        borderRadius: '8px',
        border: '1px solid var(--profile-nested-border)',
      }}
    >
      <div
        style={{
          marginBottom: '10px',
          fontWeight: 600,
          fontSize: '14px',
          color: 'var(--color-text)',
        }}
      >
        Manage roles
      </div>
      <p
        style={{
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          margin: '0 0 10px 0',
        }}
      >
        Check &quot;Show in Releases and Forecast&quot; to show the role in Releases → Capacity
        calendar and in the Forecast → <em>Roles included in distribution</em> filter.
      </p>
      {rolesList.length === 0 && availableRoles.length > 0 ? (
        <div>
          <p
            style={{
              fontSize: '13px',
              color: 'var(--color-text-secondary)',
              marginBottom: '8px',
            }}
          >
            Roles from team (not loaded from server). Load from server to manage &quot;Show in
            Releases and Forecast&quot; and Delete.
          </p>
          <ul
            style={{
              listStyle: 'disc',
              paddingLeft: '20px',
              marginBottom: '10px',
              fontSize: '14px',
              color: 'var(--color-text)',
            }}
          >
            {availableRoles.map((name) => (
              <li key={name}>
                {name.toLowerCase() === 'qa'
                  ? 'QA'
                  : name.toLowerCase() === 'developer' || name.toLowerCase() === 'dev'
                    ? 'Dev'
                    : name}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => loadRoles()}
            style={{
              padding: '8px 14px',
              fontSize: '13px',
              backgroundColor: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Load roles from server
          </button>
        </div>
      ) : rolesList.length === 0 ? (
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
          No roles. Add one with the + button next to Role.
        </p>
      ) : (
        rolesList.map((r) => (
          <div
            key={r.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px',
              padding: '6px 0',
              borderBottom: '1px solid var(--profile-nested-border)',
            }}
          >
            <label
              title="When checked, this role appears in Releases → Capacity calendar and in Forecast → Roles included in distribution filter."
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
                minWidth: '220px',
              }}
            >
              <input
                type="checkbox"
                checked={r.includeInCapacityMapping !== false}
                onChange={(e) => handleToggleRoleInMapping(r.id, e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span>Show in Releases and Forecast</span>
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span>Category:</span>
              <CustomSelect
                value={r.capacityCategory || 'developer'}
                options={[
                  { value: 'developer', label: 'Developer' },
                  { value: 'qa', label: 'QA' },
                ]}
                onChange={(val) => handleChangeCapacityCategory(r.id, val)}
                style={{ fontSize: '13px', minHeight: 'unset', padding: '3px 8px 3px 10px' }}
              />
            </label>
            {editingRoleId === r.id ? (
              <>
                <input
                  ref={renameInputRef as React.Ref<HTMLInputElement>}
                  type="text"
                  className="preserve-case-input"
                  value={editingRoleName}
                  onChange={(e) => setEditingRoleName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const raw = renameInputRef.current?.value?.trim() ?? editingRoleName;
                      if (raw) handleRenameRole(r.id, raw);
                    }
                    if (e.key === 'Escape') {
                      setEditingRoleId(null);
                      setEditingRoleName('');
                    }
                  }}
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-text)',
                    padding: '4px 8px',
                    border: '1px solid #6366f1',
                    borderRadius: '6px',
                    minWidth: '140px',
                    textTransform: 'none',
                  }}
                  autoCapitalize="words"
                  spellCheck={false}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    const raw = renameInputRef.current?.value?.trim() ?? editingRoleName;
                    if (raw) handleRenameRole(r.id, raw);
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    backgroundColor: '#6366f1',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingRoleId(null);
                    setEditingRoleName('');
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '6px',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-text)',
                }}
              >
                {r.name.toLowerCase() === 'qa'
                  ? 'QA'
                  : r.name.toLowerCase() === 'developer' || r.name.toLowerCase() === 'dev'
                    ? 'Dev'
                    : r.name}
              </span>
            )}
            {editingRoleId !== r.id && (
              <button
                type="button"
                onClick={() => {
                  setEditingRoleId(r.id);
                  setEditingRoleName(r.name);
                }}
                title="Rename role"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: '#6366f1',
                  border: '1px solid #6366f1',
                  borderRadius: '6px',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                Rename
              </button>
            )}
            <button
              type="button"
              onClick={() => handleDeleteRole(r.id)}
              title="Delete role (reassign team members first if needed)"
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                color: '#dc2626',
                border: '1px solid #dc2626',
                borderRadius: '6px',
                background: 'none',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
            >
              Delete
            </button>
          </div>
        ))
      )}
    </div>
  );
};
